import React, { useMemo, useState } from 'react';
import DataTable from '../components/DataTable.jsx';
import KPICard from '../components/KPICard.jsx';
import ModuleSettingsPanel from '../components/ModuleSettingsPanel.jsx';
import MoneyCell from '../components/MoneyCell.jsx';
import { NumericField } from '../components/NumberInput.jsx';
import ProvenanceChip from '../components/ProvenanceChip.jsx';
import SectionCard from '../components/SectionCard.jsx';
import { makeShowBlock, SubViewBreadcrumb } from '../components/SubViewShell.jsx';
import { fmtMoney } from '../engine.js';
import {
  CASH_LABELS,
  CASH_SCENARIOS,
  buildCashMonitorPlan,
  compareCashScenarios,
} from '../modules/cash-monitor/cash-monitor-model.js';
import {
  cashMonitorEvidenceSummaryView,
  cashMonitorEventPostureView,
  ledgerTimingSummaryView,
} from '../modules/cash-monitor/cash-monitor-provenance.js';
import { buildLedgerCashOpening } from '../modules/cash-monitor/ledger-cash-inputs.js';
import { buildLedgerCommercialTimingEvents } from '../modules/cash-monitor/ledger-commercial-timing-inputs.js';
import { buildScenarioCashEvents } from '../modules/cash-monitor/scenario-cash-inputs.js';

const TAB_CONTENT = 'flex flex-col [&>*+*]:mt-4';
const KPI_ROW = 'grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3';
const CONTROL_WRAP = 'bg-surface border border-border rounded-[10px] px-[1rem] py-[0.85rem] flex flex-wrap gap-3 items-center justify-between';
const CONTROL_GROUP = 'flex flex-wrap items-center gap-2';
const CONTROL_LABEL = 'text-[0.70rem] uppercase tracking-[0.08em] text-text-dim font-semibold';
const CHIP = 'inline-flex items-center gap-[5px] px-[7px] py-[3px] rounded-[4px] border text-[0.66rem] font-semibold uppercase tracking-[0.05em] whitespace-nowrap';
const ACTIVE_BUTTON = 'bg-[rgba(240,180,41,0.15)] text-accent-gold border-accent-gold/70';
const IDLE_BUTTON = 'bg-surface-el text-text-muted border-border hover:text-text hover:border-border-strong';
const EVIDENCE_WRAP = 'inline-flex min-w-0 max-w-full flex-wrap items-center gap-2 rounded-[6px] border border-border bg-surface-el/60 px-[0.55rem] py-[0.3rem] text-[0.72rem] leading-snug text-text-muted';
const EVIDENCE_TEXT = 'min-w-[10rem] max-w-full flex-1 whitespace-normal break-words';

const FEATURE_DEFAULTS = {
  dailyPlan: true,
  receipts: true,
  obligations: true,
  scenarios: true,
  warnings: true,
};

function localDateKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function cashMonitorScenarioWithDefaults(scenario) {
  const existing = scenario?.moduleSettings?.['cash-monitor']?.features || {};
  return {
    ...scenario,
    moduleSettings: {
      ...(scenario?.moduleSettings || {}),
      'cash-monitor': {
        ...(scenario?.moduleSettings?.['cash-monitor'] || {}),
        features: { ...FEATURE_DEFAULTS, ...existing },
      },
    },
  };
}

function LabelPill({ label }) {
  const tone = label === CASH_LABELS.ACTUAL_CASH
    ? 'bg-[rgba(96,165,250,0.10)] text-accent-blue border-[rgba(96,165,250,0.35)]'
    : label === CASH_LABELS.EXPECTED_RECEIPTS
      ? 'bg-[rgba(74,222,128,0.10)] text-accent-green border-[rgba(74,222,128,0.35)]'
      : label === CASH_LABELS.COMMITTED_OBLIGATIONS
        ? 'bg-[rgba(248,113,113,0.10)] text-accent-red border-[rgba(248,113,113,0.35)]'
        : label === CASH_LABELS.OPTIONAL_PAYMENTS
          ? 'bg-[rgba(240,180,41,0.10)] text-accent-gold border-[rgba(240,180,41,0.35)]'
          : 'bg-surface-el text-text-muted border-border';
  return <span className={`${CHIP} ${tone}`}>{label}</span>;
}

function WarningLabel({ value }) {
  const tone = value === 'Clear'
    ? 'bg-[rgba(74,222,128,0.10)] text-accent-green border-[rgba(74,222,128,0.35)]'
    : value.includes('gap') || value.includes('Negative')
      ? 'bg-[rgba(248,113,113,0.10)] text-accent-red border-[rgba(248,113,113,0.35)]'
      : 'bg-[rgba(240,180,41,0.10)] text-accent-gold border-[rgba(240,180,41,0.35)]';
  return <span className={`${CHIP} normal-case tracking-normal ${tone}`}>{value}</span>;
}

function SegmentedButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border px-[0.65rem] py-[0.35rem] rounded-sm text-[0.78rem] font-semibold cursor-pointer transition-colors ${active ? ACTIVE_BUTTON : IDLE_BUTTON}`}
    >
      {children}
    </button>
  );
}

function formatRunway(kpis) {
  return `${kpis.runwayDays}${kpis.runwayCapped ? '+' : ''} days`;
}

function cashFloorTone(value, minimumCashFloor) {
  if (value < 0) return 'bad';
  if (value < minimumCashFloor) return 'warn';
  return 'good';
}

function labelsText(labels) {
  return (labels || []).join(' + ');
}

function EventPostureCell({ event }) {
  const view = cashMonitorEventPostureView(event);
  return (
    <span data-testid="cash-monitor-event-posture" className="inline-flex max-w-[260px] flex-wrap items-center gap-1.5">
      <ProvenanceChip kind={view.chipKind} tooltip={view.tooltip} compact />
      <span className="text-text-muted">{view.sourceLabel}</span>
      <span className="rounded-[3px] border border-border bg-surface-el px-[5px] py-[1px] text-[0.64rem] font-semibold uppercase tracking-[0.05em] text-text-dim">
        {view.postureLabel}
      </span>
      <span className="rounded-[3px] border border-[rgba(240,180,41,0.35)] bg-[rgba(240,180,41,0.10)] px-[5px] py-[1px] text-[0.64rem] font-semibold uppercase tracking-[0.05em] text-accent-gold">
        {view.truthLabel}
      </span>
    </span>
  );
}

export default function CashMonitorTab({ scenario, setScenario, activeSection, onBackToOverview }) {
  const show = makeShowBlock(activeSection);
  const [horizonDays, setHorizonDays] = useState(45);
  const [scenarioId, setScenarioId] = useState('agreed-schedule');
  const [includeOptionalPayments, setIncludeOptionalPayments] = useState(false);
  const [minimumCashFloor, setMinimumCashFloor] = useState(5000);
  const startDate = useMemo(() => localDateKey(), []);
  const settingsScenario = useMemo(() => cashMonitorScenarioWithDefaults(scenario), [scenario]);
  const features = settingsScenario.moduleSettings['cash-monitor'].features;

  const openingCash = useMemo(
    () => buildLedgerCashOpening({ scenario }),
    [scenario],
  );
  const scenarioEvents = useMemo(
    () => buildScenarioCashEvents({ scenario, startDate, horizonDays }),
    [scenario, startDate, horizonDays],
  );
  const ledgerTimingLoadKey = `${settingsScenario?._ledgerReady ? 'ready' : 'pending'}:${settingsScenario?._ledgerSource || 'unset'}`;
  const ledgerCommercialTimingEvents = useMemo(
    () => buildLedgerCommercialTimingEvents({ startDate, horizonDays }),
    [startDate, horizonDays, ledgerTimingLoadKey],
  );
  const events = useMemo(
    () => [...scenarioEvents, ...ledgerCommercialTimingEvents],
    [scenarioEvents, ledgerCommercialTimingEvents],
  );
  const plan = useMemo(
    () => buildCashMonitorPlan({
      startDate,
      horizonDays,
      openingCash,
      events,
      scenarioId,
      includeOptionalPayments,
      minimumCashFloor,
    }),
    [startDate, horizonDays, openingCash, events, scenarioId, includeOptionalPayments, minimumCashFloor],
  );
  const comparison = useMemo(
    () => compareCashScenarios({
      startDate,
      horizonDays,
      openingCash,
      events,
      includeOptionalPayments,
      minimumCashFloor,
    }),
    [startDate, horizonDays, openingCash, events, includeOptionalPayments, minimumCashFloor],
  );
  const activeEvents = useMemo(
    () => plan.rows.flatMap((row) => row.events.map((event) => ({ ...event, date: row.date }))),
    [plan.rows],
  );
  const receiptEvents = activeEvents.filter((event) => event.bucket === 'expectedReceipts');
  const obligationEvents = activeEvents.filter((event) => event.bucket === 'committedObligations');
  const ledgerTimingSummary = useMemo(
    () => ledgerTimingSummaryView(ledgerCommercialTimingEvents),
    [ledgerCommercialTimingEvents],
  );
  const evidenceSummary = useMemo(
    () => cashMonitorEvidenceSummaryView({
      openingCash,
      events: activeEvents,
      labelIssues: plan.labelIssues,
    }),
    [openingCash, activeEvents, plan.labelIssues],
  );

  const moneyCol = (key, label, toneForValue = null) => ({
    key,
    align: 'right',
    format: (value, row) => (
      <MoneyCell
        value={value}
        decimals={0}
        label={label || key}
        row={row}
        tone={toneForValue ? toneForValue(value, row) : undefined}
      />
    ),
  });

  const eventColumns = [
    { key: 'date', label: 'Date', width: '12%' },
    { key: 'title', label: 'Line' },
    moneyCol('amount', 'Cash event amount'),
    { key: 'posture', label: 'Posture', format: (_value, event) => <EventPostureCell event={event} /> },
    { key: 'source', label: 'Source', className: 'text-text-muted' },
    { key: 'labelsText', label: 'Labels' },
  ];

  return (
    <div className={TAB_CONTENT}>
      <SubViewBreadcrumb moduleId="cash-monitor" activeSection={activeSection} onBack={onBackToOverview} />

      {!activeSection && (
        <ModuleSettingsPanel
          scenario={settingsScenario}
          setScenario={setScenario}
          moduleId="cash-monitor"
          title="Cash Monitor - module settings"
          features={[
            { key: 'dailyPlan', label: 'Daily plan', hint: 'Opening cash, flows, net change, closing cash, warnings' },
            { key: 'receipts', label: 'Receipts', hint: 'Expected receipts with assumption labels' },
            { key: 'obligations', label: 'Obligations', hint: 'Committed obligations by active cash plan variant' },
            { key: 'scenarios', label: 'Plan variants', hint: 'Due date, agreed schedule, tight budget' },
            { key: 'warnings', label: 'Warnings', hint: 'Cash gaps and floor breaks' },
          ]}
        />
      )}

      <div className={CONTROL_WRAP}>
        <div className={CONTROL_GROUP}>
          <span className={CONTROL_LABEL}>Plan variant</span>
          {CASH_SCENARIOS.map((item) => (
            <SegmentedButton
              key={item.id}
              active={scenarioId === item.id}
              onClick={() => setScenarioId(item.id)}
            >
              {item.label}
            </SegmentedButton>
          ))}
        </div>
        <div className={CONTROL_GROUP}>
          <span className={CONTROL_LABEL}>Horizon</span>
          {[30, 45, 60, 90].map((days) => (
            <SegmentedButton
              key={days}
              active={horizonDays === days}
              onClick={() => setHorizonDays(days)}
            >
              {days}d
            </SegmentedButton>
          ))}
        </div>
        <label className="flex items-center gap-2 text-[0.78rem] text-text-muted">
          <input
            type="checkbox"
            checked={includeOptionalPayments}
            onChange={(event) => setIncludeOptionalPayments(event.target.checked)}
            className="accent-accent-gold cursor-pointer"
          />
          Include optional payments
        </label>
        <label className="flex items-center gap-2 text-[0.78rem] text-text-muted">
          <span className={CONTROL_LABEL}>Cash floor</span>
          <NumericField
            value={minimumCashFloor}
            min={0}
            decimals={0}
            onChange={(value) => setMinimumCashFloor(Number(value) || 0)}
            className="w-[110px] bg-surface-el border border-border rounded-sm px-[0.5rem] py-[0.3rem] text-text font-mono text-[0.78rem]"
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.values(CASH_LABELS).map((label) => <LabelPill key={label} label={label} />)}
        <span className="inline-flex items-center gap-2 text-[0.72rem] text-text-muted px-[0.55rem]">
          Source: <ProvenanceChip kind={openingCash.provenanceKind} compact />
          <span>{openingCash.evidence.sourceStateLabel}</span>
        </span>
        <span
          data-testid="cash-monitor-evidence-summary"
          className={EVIDENCE_WRAP}
          title={evidenceSummary.tooltip}
        >
          <ProvenanceChip kind={evidenceSummary.chipKind} tooltip={evidenceSummary.tooltip} compact />
          <span className={EVIDENCE_TEXT}>{evidenceSummary.label}</span>
          <span className={evidenceSummary.labelIssueCount > 0 ? 'font-semibold text-accent-red' : 'font-semibold text-accent-green'}>
            {evidenceSummary.status}
          </span>
        </span>
        {ledgerTimingSummary.count > 0 && (
          <span
            data-testid="cash-monitor-ledger-timing-proof"
            className={EVIDENCE_WRAP}
            title={ledgerTimingSummary.tooltip}
          >
            <ProvenanceChip kind={ledgerTimingSummary.chipKind} tooltip={ledgerTimingSummary.tooltip} compact />
            <span className={EVIDENCE_TEXT}>{ledgerTimingSummary.label}</span>
            <span className="font-semibold text-accent-gold">{ledgerTimingSummary.posture}</span>
          </span>
        )}
      </div>

      <div className={KPI_ROW}>
        <KPICard
          label="Opening evidence cash"
          value={<MoneyCell value={plan.kpis.openingActualCash} decimals={0} label="Opening evidence cash" />}
          sub={plan.kpis.openingSourceLabel}
          provenance={openingCash.provenanceKind}
          provenanceTip="Opening evidence cash follows the current Lens Ledger/source-state posture. Source-local/demo is labeled when strict Ledger proof is unavailable."
        />
        <KPICard
          label="Lowest projected cash"
          value={<MoneyCell value={plan.kpis.lowestProjectedCashAmount} decimals={0} tone={cashFloorTone(plan.kpis.lowestProjectedCashAmount, minimumCashFloor)} label="Lowest projected cash" />}
          sub={plan.kpis.lowestProjectedCashDate || 'No projected date'}
          tone={cashFloorTone(plan.kpis.lowestProjectedCashAmount, minimumCashFloor)}
          provenance="computed"
        />
        <KPICard
          label="First cash gap"
          value={plan.kpis.firstCashGapDate || 'None'}
          sub={plan.kpis.firstCashGapAmount == null ? 'No negative close in horizon' : fmtMoney(plan.kpis.firstCashGapAmount, { decimals: 0 })}
          tone={plan.kpis.firstCashGapDate ? 'bad' : 'good'}
          provenance="computed"
        />
        <KPICard
          label="Runway days"
          value={formatRunway(plan.kpis)}
          sub={`${horizonDays}-day horizon from ${startDate}`}
          tone={plan.kpis.firstCashGapDate ? 'bad' : 'good'}
          provenance="computed"
        />
        <KPICard
          label="Expected receipts 14/30"
          value={<MoneyCell value={plan.kpis.expectedReceiptsNext14} decimals={0} label="Expected receipts next 14 days" />}
          sub={`30d: ${fmtMoney(plan.kpis.expectedReceiptsNext30, { decimals: 0 })}`}
          tone="good"
          provenance="local"
          provenanceTip="Expected receipts include strict-labeled Lens model rows and Ledger SO pre-posting commercial timing signals."
        />
        <KPICard
          label="Committed obligations 14/30"
          value={<MoneyCell value={plan.kpis.committedObligationsNext14} decimals={0} label="Committed obligations next 14 days" />}
          sub={`30d: ${fmtMoney(plan.kpis.committedObligationsNext30, { decimals: 0 })}`}
          tone="warn"
          provenance="local"
          provenanceTip="Committed obligations include strict-labeled Lens model rows and Ledger PO pre-posting commercial timing signals."
        />
      </div>

      {show('daily-plan') && features.dailyPlan && (
        <SectionCard
          sectionId="daily-plan"
          title="Daily cash plan"
          subtitle={`Active plan variant: ${CASH_SCENARIOS.find((item) => item.id === scenarioId)?.label || scenarioId} - ${horizonDays} days`}
        >
          <DataTable
            compact
            columns={[
              { key: 'date', label: 'Date', width: '10%' },
              { ...moneyCol('openingCash', 'Opening cash'), label: 'Opening cash' },
              { key: 'actualCashMarker', label: 'Evidence cash marker' },
              { ...moneyCol('expectedReceipts', 'Expected receipts'), label: 'Expected receipts' },
              { ...moneyCol('committedObligations', 'Committed obligations'), label: 'Committed obligations' },
              { ...moneyCol('optionalPayments', 'Optional payments'), label: 'Optional payments' },
              { ...moneyCol('netChange', 'Net cash change', (value) => value >= 0 ? 'good' : 'bad'), label: 'Net change' },
              { ...moneyCol('closingCash', 'Closing cash', (value) => value >= minimumCashFloor ? 'good' : value < 0 ? 'bad' : 'warn'), label: 'Closing cash' },
              { key: 'warningLabel', label: 'Warning label', format: (value) => <WarningLabel value={value} /> },
            ]}
            rows={plan.rows}
            pagination
            pageSize={30}
            paginationLabel="Daily cash plan rows"
          />
        </SectionCard>
      )}

      {show('receipts') && features.receipts && (
        <SectionCard
          sectionId="receipts"
          title="Expected receipts"
          subtitle="Assumption-labeled rows from Lens model inputs and Ledger SO commercial timing"
        >
          <DataTable
            compact
            columns={[
              ...eventColumns,
              { key: 'collectionRateText', label: 'Collection' },
            ]}
            rows={receiptEvents.map((event) => ({
              ...event,
              labelsText: labelsText(event.labels),
              collectionRateText: `${Math.round((event.collectionRate ?? 1) * 100)}%`,
            }))}
            pagination
            pageSize={25}
            paginationLabel="Expected receipt rows"
          />
        </SectionCard>
      )}

      {show('obligations') && features.obligations && (
        <SectionCard
          sectionId="obligations"
          title="Committed obligations"
          subtitle="Active model rows and Ledger PO commercial timing; posted actuals remain separate"
        >
          <DataTable
            compact
            columns={eventColumns}
            rows={obligationEvents.map((event) => ({
              ...event,
              labelsText: labelsText(event.labels),
            }))}
            pagination
            pageSize={25}
            paginationLabel="Committed obligation rows"
          />
        </SectionCard>
      )}

      {show('plan-variants') && features.scenarios && (
        <SectionCard
          sectionId="plan-variants"
          title="Plan variant comparison"
          subtitle="Pay-on-due-date vs agreed-schedule vs tight-budget plan"
        >
          <DataTable
            compact
            columns={[
              { key: 'label', label: 'Plan variant' },
              { ...moneyCol('lowestProjectedCashAmount', 'Lowest projected cash'), label: 'Lowest cash' },
              { key: 'lowestProjectedCashDate', label: 'Lowest date' },
              { key: 'firstCashGapDate', label: 'First gap' },
              { key: 'runwayDays', label: 'Runway' },
              { ...moneyCol('expectedReceiptsNext30', 'Expected receipts next 30 days'), label: 'Receipts 30d' },
              { ...moneyCol('committedObligationsNext30', 'Committed obligations next 30 days'), label: 'Obligations 30d' },
              { ...moneyCol('optionalPaymentsNext30', 'Optional payments next 30 days'), label: 'Optional 30d' },
              { ...moneyCol('closingCash', 'Closing cash'), label: 'Closing cash' },
              { key: 'warningLabel', label: 'Warning label', format: (value) => <WarningLabel value={value} /> },
            ]}
            rows={comparison}
          />
        </SectionCard>
      )}

      {show('warnings') && features.warnings && (
        <SectionCard
          sectionId="warnings"
          title="Warnings"
          subtitle={`Cash floor ${fmtMoney(minimumCashFloor, { decimals: 0 })} - ${plan.warnings.count} warning rows`}
        >
          {plan.warnings.count === 0 ? (
            <div className="px-[0.7rem] py-[0.55rem] bg-surface-el border border-border rounded-sm text-[0.82rem] text-text-muted">
              No cash gaps or floor breaks in the active horizon.
            </div>
          ) : (
            <DataTable
              compact
              columns={[
                { key: 'date', label: 'Date' },
                { key: 'label', label: 'Warning label', format: (value) => <WarningLabel value={value} /> },
                { ...moneyCol('closingCash', 'Warning closing cash'), label: 'Closing cash' },
                { ...moneyCol('minimumCashFloor', 'Minimum cash floor'), label: 'Cash floor' },
              ]}
              rows={plan.warnings.warnings.map((warning) => ({ ...warning, id: warning.date }))}
              pagination
              pageSize={25}
              paginationLabel="Cash warning rows"
            />
          )}
          {plan.labelIssues.length > 0 && (
            <div className="mt-3 px-[0.7rem] py-[0.55rem] bg-[rgba(248,113,113,0.08)] border border-[rgba(248,113,113,0.35)] rounded-sm text-[0.78rem] text-accent-red">
              Label validation issue count: {plan.labelIssues.length}
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
}
